import { Injectable } from '@angular/core';
import { DTypeFormSelectField } from '@grupo-constant/next-dtype/form';
import { DType, DTypeStatus, NextLocator, Partaker, SelectOption, SelectOptionGroup } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { map, Observable } from 'rxjs';
import { DTypeService } from '../dtype/dtype.service';
import { NextMethodsEnum } from '../enums/next-methods.enum';
import { HttpService } from '../http/http.service';
import {
    FlushNextTaskTypeReply,
    NextTaskType,
    NextTaskTypeDN,
    NextTaskTypeGoal,
    RetrieveNewTaskTypeInstaceBody,
    RetrieveNewTaskTypeInstanceReply,
    RetrieveNextTaskTypeCollectionDN,
    RetrieveNextTaskTypeReply,
    TaskTypeTypology,
    TASK_TYPE_FORM_URI, TASK_TYPE_GOAL_URI, TASK_TYPE_URI, RetrieveCompatibleNextTaskTypeReply
} from './task-type.types';
import { OU_URI } from '../../modules/admin/ou/ou.types';
import { DTYPE_BRAND_URI, DTYPE_CUSTOMER_URI, DTYPE_PROJECT_URI } from '../../modules/admin/projects/projects.service';
import { SERVICE_DTYPE_URI } from '../../modules/admin/services/services.service';

export const taskTypeWithTimeUri = 'tasktypology://com.cloudcodice.thirdparty.grupoconstant.nexttasktype.typology.NextTaskTypeWithTime';
export const taskTypeWithoutTimeUri = 'tasktypology://com.cloudcodice.thirdparty.grupoconstant.nexttasktype.typology.NextTaskTypeWithoutTime';
export const taskTypeAssignment = 'tasktypology://com.cloudcodice.thirdparty.grupoconstant.nexttasktype.typology.NextTaskTypeTaskAssignment';
export const taskTypeWorkshift = 'tasktypology://com.cloudcodice.thirdparty.grupoconstant.nexttasktype.typology.NextTaskTypeWorkshift';
@Injectable({
    providedIn: 'root'
})
export class TaskTypeService {

    constructor(
        private dtypeService: DTypeService,
        private http: HttpService,
        private transloco: TranslocoService,
    ) { }

    retrieveTaskTypeById(taskTypeId: string, date?: string): Observable<NextTaskType> {
        return this.http.post<RetrieveNextTaskTypeReply>(NextMethodsEnum.retrieveNextTaskTypeById, { taskTypeId, date }).pipe(
            map(reply => reply.nextTaskType ?? { forms: [], goals: [], dtype: { column: [], path: [], uri: 'taskTypeExtra://' } } as NextTaskType)
        );
    }

    retrieveCompatibleTaskType(serviceId: string, taskTypeUri: string): Observable<SelectOption[]> {
        return this.http.post<RetrieveCompatibleNextTaskTypeReply>(NextMethodsEnum.retrieveCompatibleTaskTypes, {serviceId, taskTypeUri}).pipe(
            map(reply => reply.taskTypeOptions ?? [])
        );
    }

    retrieveTaskTypeList(locator: NextLocator) {
        return this.http.post<RetrieveNextTaskTypeCollectionDN>(NextMethodsEnum.retrieveNextTaskTypeCollectionDN, { locator });
    }

    retrieveTaskTypeListCollectionDN(locator: NextLocator): Observable<[NextTaskTypeDN, Partaker]> {
        return this.http.post<RetrieveNextTaskTypeCollectionDN>(NextMethodsEnum.retrieveNextTaskTypeCollectionDN, { locator })
            .pipe(map(reply => [reply?.collection ?? {}, reply?.partaker ?? {}]));
    }

    retrieveTaskTypeDType(locator?: NextLocator) {
        return this.dtypeService.getDatatype({ retrieveDTypeBody: { uri: TASK_TYPE_URI, status: DTypeStatus.DESIGN, locator, bindableOptions: true }, includeEmbedded: true });
    }

    retrieveTaskTypeDtypeForm(values: NextTaskType, locator?: NextLocator, options?: { readonly?: boolean; isUpdate: boolean }) {
		if (values.typology === TaskTypeTypology.withTime) {
			return this.dtypeService.getDtypeFormByUri({ uri: taskTypeWithTimeUri,
            status: DTypeStatus.DESIGN, locator, bindableOptions: true }, values, options);
		} else if (values.typology === TaskTypeTypology.withoutTime) {
			return this.dtypeService.getDtypeFormByUri({ uri: taskTypeWithoutTimeUri,
            status: DTypeStatus.DESIGN, locator, bindableOptions: true }, values, options);
		} else if (values.typology === TaskTypeTypology.taskAssigment) {
			return this.dtypeService.getDtypeFormByUri({ uri: taskTypeAssignment,
            status: DTypeStatus.DESIGN, locator, bindableOptions: true }, values, options);
		} else if (values.typology === TaskTypeTypology.workshift) {
			return this.dtypeService.getDtypeFormByUri({ uri: taskTypeWorkshift,
            status: DTypeStatus.DESIGN, locator, bindableOptions: true }, values, options);

        }
        return this.dtypeService.getDtypeFormByUri({ uri: TASK_TYPE_URI, status: DTypeStatus.DESIGN, locator, bindableOptions: true }, values, options);
    }

    retrieveTaskTypeFormDType(locator: NextLocator) {
        return this.dtypeService.getDatatype({ retrieveDTypeBody: { uri: TASK_TYPE_FORM_URI, status: DTypeStatus.DESIGN, locator, bindableOptions: true }, includeEmbedded: true });
    }

    retrieveTaskTypeGoalDType(locator: NextLocator) {
        return this.dtypeService.getDatatype({ retrieveDTypeBody: { uri: TASK_TYPE_GOAL_URI, status: DTypeStatus.DESIGN, locator, bindableOptions: true }, includeEmbedded: true });
    }

    async retrieveTaskTypeFormDTypeForm(values: any, locator: NextLocator, options?: { readonly?: boolean; isUpdate: boolean }) {
        const dtypeForm = await this.dtypeService.getDtypeFormByUri({ uri: TASK_TYPE_FORM_URI, status: DTypeStatus.DESIGN, locator, bindableOptions: true }, values, options);
        for (const group of (dtypeForm.fields.formUri as DTypeFormSelectField).groups ?? []) {
            group.value = this.addNameToGroup(group);
        }
        return dtypeForm;
    }

    retrieveTaskTypeGoalDTypeFormFromDType(dtype: DType, values: NextTaskTypeGoal, options?: { readonly?: boolean; isUpdate: boolean }) {
        return this.dtypeService.getDtypeFormFromDtype(dtype , values, options);
    }

    retrieveTaskTypeGoalDTypeForm(values: NextTaskTypeGoal, locator: NextLocator, options?: { readonly?: boolean; isUpdate: boolean }) {
        return this.dtypeService.getDtypeFormByUri({ uri: TASK_TYPE_GOAL_URI, status: DTypeStatus.DESIGN, locator, bindableOptions: true }, values, options);
    }

    flushTaskType(nextTaskType: NextTaskType, locator: NextLocator) {
        return this.http.post<FlushNextTaskTypeReply>(NextMethodsEnum.flushNextTaskType, { nextTaskType, locator }).pipe(
            map(reply => reply.nextTaskType)
        );
    }

    retrieveNewTaskTypeInstance(body: RetrieveNewTaskTypeInstaceBody) {
        return this.http.post<RetrieveNewTaskTypeInstanceReply>(NextMethodsEnum.retrieveNewNextTaskTypeInstance, body);
    }

    emitUpdateSidebarEvent() {
        this.dtypeService.emitUpdateSidebarEvent();
    }

    getSubtitleFromLocator(locator: string, partaker: Partaker): string {
        const split = locator.split(',');
        const firstLocatorSplit = split[0].split('=');
        let secondLocatorSplit: string[];
        if (split.length > 1) {
            secondLocatorSplit = split[1].split('='); // Solo para clientes, se corresponde con marca
        }
        switch (firstLocatorSplit[0]) {
            case 'OU':
                return partaker[OU_URI][firstLocatorSplit[1]]?.name;
            case 'Brand':
                return partaker[DTYPE_BRAND_URI][firstLocatorSplit[1]]?.marca_name;
            case 'Customer':
                return partaker[DTYPE_CUSTOMER_URI][`${secondLocatorSplit[1]}+${firstLocatorSplit[1]}`]?.name;
            case 'Project':
                return partaker[DTYPE_PROJECT_URI][firstLocatorSplit[1]]?.name;
            case 'Service':
                return partaker[SERVICE_DTYPE_URI][firstLocatorSplit[1]]?.name;
            default:
                return '';
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Methods for generate sidebar menus for brands, customer...
    // -----------------------------------------------------------------------------------------------------

    getLocatorFromString(stringLocator: string): NextLocator {
        const locator: NextLocator = {};
        const splitLocator = stringLocator.split(/[/,]/ig);
        let internalSplit: string[];
        for (const split of splitLocator) {
            internalSplit = split.split('=');
            if (internalSplit.length > 1) {
                switch (internalSplit[0]) {
                    case 'OU':
                        locator.OU = internalSplit[1];
                        break;
                    case 'Brand':
                        locator.marca_code = internalSplit[1];
                        break;
                    case 'Customer':
                        locator.cliente_code = Number(internalSplit[1]);
                        break;
                    case 'Project':
                        locator.projectId = internalSplit[1];
                        break;
                    case 'Service':
                        locator.serviceId = internalSplit[1];
                        break;
                }
            }
        }
        return locator;
    }

    private addNameToGroup(group: SelectOptionGroup) {
        if (group.option[0].code.includes('Service=')) {
            return this.transloco.translate('service') + ': ' + group.value;
        } else if (group.option[0].code.includes('Project=')) {
            return this.transloco.translate('project') + ': ' + group.value;
        } else if (group.option[0].code.includes('Customer=')) {
            return this.transloco.translate('customer') + ': ' + group.value;
        } else if (group.option[0].code.includes('Brands=')) {
            return this.transloco.translate('brand') + ': ' + group.value;
        } else {
            return 'OU: ' + group.value;
        }
    }
}
